import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { motion } from 'framer-motion';
import React__default, { useRef, useState, useEffect } from 'react';
import { styled } from 'styled-components';
import { IconDesktop16KindImage, IconDesktop16MiscUpDownArrow } from '@fabric-app/icons/svg';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { SmallControlButton } from '@fabric-app/ui/components/Buttons/SmallControlButton';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { usePanning } from '@fabric-app/ui/hooks/usePanning';

const CoverPicker = ({ onFileSelect, isUploading, text }) => {
  const inputRef = useRef(null);
  if (!onFileSelect) return null;
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsxs(SmallControlButton, {
        disabled: isUploading,
        onClick: () => inputRef.current?.click(),
        children: [
          /* @__PURE__ */ jsx(IconDesktop16KindImage, {}),
          text
        ]
      }),
      /* @__PURE__ */ jsx("input", {
        ref: inputRef,
        type: "file",
        style: {
          display: "none"
        },
        accept: "image/*",
        onChange: (e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length < 1) return;
          onFileSelect(files[0]);
        }
      })
    ]
  });
};
const DragOverlay = styled(motion.div).withConfig({
  displayName: "CoverImage__DragOverlay",
  componentId: "sc-14431ca7-0"
})([
  `position:absolute;inset:0;background-color:rgba(0,0,0,0.1);z-index:8;display:flex;align-items:center;justify-content:center;cursor:grab;touch-action:none;`
]);
const DragOverlayInfo = styled.div.withConfig({
  displayName: "CoverImage__DragOverlayInfo",
  componentId: "sc-14431ca7-1"
})([
  `display:flex;flex-direction:row;justify-content:center;align-items:center;padding:6px;gap:6px;background:#000000;opacity:0.6;border-radius:6px;font-weight:500;font-size:14px;line-height:17px;color:#ffffff;`
]);
const DragPosition = ({ onDeltaYChange }) => {
  const { motionProps } = usePanning({
    onPan: (e, info) => {
      onDeltaYChange(info.delta.y);
    },
    touchOnly: false
  });
  return /* @__PURE__ */ jsx(DragOverlay, {
    ...motionProps,
    children: /* @__PURE__ */ jsx(DragOverlayInfo, {
      children: "Drag image to position "
    })
  });
};
const getMaxMovableYOffsetInOneDirection = (coverHeight, imageHeight) => {
  if (!coverHeight || !imageHeight || coverHeight > imageHeight) {
    return 0;
  }
  return (imageHeight - coverHeight) / imageHeight / 2 * 100;
};
const DRAG_SPEED = 0.2;
const CoverImage = ({ url, onFileSelect, isUploading, onYOffsetPctChange, yOffsetPct }) => {
  const isRepositioningState = useBoolState(false);
  const [yOffset, setYOffset] = useState(0);
  const coverRef = useRef(null);
  const [coverRect] = useDomContentRect(coverRef);
  const imageRef = useRef(null);
  const [imageRect] = useDomContentRect(imageRef);
  const maxMovableYOffsetInOneDirection = getMaxMovableYOffsetInOneDirection(coverRect?.height, imageRect?.height);
  const changeOffsetBy = React__default.useCallback((y, options) => {
    setYOffset((prev = 0) => {
      const offset = options?.fromZero ? y : y * DRAG_SPEED + prev;
      return Math.min(Math.max(offset, -maxMovableYOffsetInOneDirection), maxMovableYOffsetInOneDirection);
    });
  }, [
    maxMovableYOffsetInOneDirection
  ]);
  useEffect(() => {
    changeOffsetBy(yOffsetPct ?? 0, {
      fromZero: true
    });
  }, [
    changeOffsetBy,
    yOffsetPct
  ]);
  return /* @__PURE__ */ jsxs(CoverContainer, {
    ref: coverRef,
    children: [
      isUploading && /* @__PURE__ */ jsx(UploadingOverlay, {
        children: /* @__PURE__ */ jsx(Spinner, {
          size: 24
        })
      }),
      isRepositioningState.value ? /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(DragPosition, {
            onDeltaYChange: changeOffsetBy
          }),
          /* @__PURE__ */ jsxs(Controls, {
            children: [
              /* @__PURE__ */ jsx(SmallControlButton, {
                onClick: () => {
                  isRepositioningState.handleFalse();
                  onYOffsetPctChange?.(yOffset);
                },
                children: "Save position"
              }),
              /* @__PURE__ */ jsx(SmallControlButton, {
                onClick: () => {
                  isRepositioningState.handleFalse();
                  setYOffset(yOffsetPct ?? 0);
                },
                children: "Cancel"
              })
            ]
          })
        ]
      }) : /* @__PURE__ */ jsxs(BaseControls, {
        children: [
          onFileSelect && /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsx(SmallControlButton, {
                onClick: () => onFileSelect(null),
                children: "Remove"
              }),
              /* @__PURE__ */ jsx(CoverPicker, {
                onFileSelect,
                isUploading,
                text: "Change cover"
              })
            ]
          }),
          onYOffsetPctChange && /* @__PURE__ */ jsxs(SmallControlButton, {
            onClick: isRepositioningState.handleTrue,
            children: [
              /* @__PURE__ */ jsx(IconDesktop16MiscUpDownArrow, {}),
              "Reposition"
            ]
          })
        ]
      }),
      url && /* @__PURE__ */ jsx(CoverImg, {
        style: {
          transform: `translateY(${yOffset}%)`
        },
        ref: imageRef,
        draggable: false,
        src: url,
        alt: "cover"
      })
    ]
  });
};
const CoverImg = styled.img.withConfig({
  displayName: "CoverImage__CoverImg",
  componentId: "sc-14431ca7-2"
})([
  `width:100%;object-fit:cover;height:auto;border-radius:0;`
]);
const Controls = styled.div.withConfig({
  displayName: "CoverImage__Controls",
  componentId: "sc-14431ca7-3"
})([
  `position:absolute;top:0.625rem;right:0.625rem;display:flex;gap:0.5rem;z-index:10;transition:0.2s all ease-in-out;`
]);
const BaseControls = styled(Controls).withConfig({
  displayName: "CoverImage__BaseControls",
  componentId: "sc-14431ca7-4"
})([
  ``
]);
const CoverContainer = styled.div.withConfig({
  displayName: "CoverImage__CoverContainer",
  componentId: "sc-14431ca7-5"
})([
  `position:relative;width:100%;aspect-ratio:160 / 32;`,
  `{aspect-ratio:370 / 160;margin-bottom:24px;}overflow:hidden;display:flex;align-items:center;`,
  `{opacity:0;}&:hover{`,
  `{opacity:1;}}`
], mediaMobile, BaseControls, BaseControls);
const UploadingOverlay = styled.div.withConfig({
  displayName: "CoverImage__UploadingOverlay",
  componentId: "sc-14431ca7-6"
})([
  `position:absolute;inset:0;background-color:rgba(0,0,0,0.2);display:flex;align-items:center;justify-content:center;z-index:12;`
]);

export { CoverImage, CoverPicker };
//# sourceMappingURL=CoverImage.js.map
